import { useTranslation } from 'react-i18next';

import {
    Body2,
    FilledCard,
    RichText,
    Stack,
    Subtitle1,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';

import { useMedicationConfigOverride } from '../../../../contexts/DrugProgramProvider';
import { useMedicationDisclaimers } from '../../../../hooks/use-medication-disclosures';
import { useProgramTerms } from '../../../../hooks/use-program-terms';
import { processProviderToPharmacyInfo } from '../../../../util';
import { usePriceQualifier } from '../../fill/common/use-price-qualifier';

import type { PricingComponentProps } from './pricing-types';
import { PricingTermsLink } from './PricingTermsLink';

export const InsurancePricing = ({
    medication,
    price,
    priceType,
    provider,
}: PricingComponentProps) => {
    const { t } = useTranslation('lilly');

    const terms = useProgramTerms(medication, 'insurance');

    const configOverride = useMedicationConfigOverride();
    const pharmacyInfo = processProviderToPharmacyInfo(
        provider,
        configOverride
    );
    const pharmacyName = pharmacyInfo.name;

    const priceQualifier = usePriceQualifier({
        medication,
        patientPay: price,
        priceType,
    });

    const richDisclaimers = useMedicationDisclaimers({
        headlinePriceType: priceType,
        medication,
        slot: 'price-card',
    });

    const showRichDisclaimer = !!richDisclaimers.length;

    return (
        <>
            <FilledCard
                withBorder
                color="primary"
                data-testid="pharmacy-pricing-card"
            >
                <Stack>
                    {price === null ||
                    priceType === 'InsuredPriceAtPharmacy' ? (
                        <Subtitle2>
                            {t('fill.pricing.finalPriceAtPharmacy')}
                        </Subtitle2>
                    ) : (
                        <>
                            {terms?.savingsCard ? (
                                <Subtitle2>
                                    {t('fill.insurance.pricing.withCard', {
                                        cardName: terms.savingsCard.name,
                                    })}
                                </Subtitle2>
                            ) : (
                                <Subtitle2>
                                    {t('fill.insurance.pricing.withInsurance')}
                                </Subtitle2>
                            )}
                            <Subtitle1>
                                {t('fill.pricing.asLowAs', {
                                    price,
                                    qualifier: priceQualifier?.text ?? '',
                                })}
                            </Subtitle1>
                        </>
                    )}
                    {priceQualifier?.description ? (
                        <RichText {...priceQualifier.description} />
                    ) : null}
                </Stack>
            </FilledCard>
            <Stack data-testid="pharmacy-pricing-terms-and-conditions">
                {showRichDisclaimer ? (
                    richDisclaimers.map((richDisclaimer) => {
                        const richContent = richDisclaimer.content;

                        return richContent.map(({ key, ...richLine }) => (
                            <RichText key={key} {...richLine} />
                        ));
                    })
                ) : (
                    <Body2>
                        {t('fill.pricing.feesMayApplyWithPharmacy', {
                            pharmacyName,
                            termsDisclaimer: '',
                        })}
                    </Body2>
                )}
                <PricingTermsLink
                    medication={medication}
                    pathway="insurance"
                    terms={terms}
                />
            </Stack>
        </>
    );
};
